import type { DropinConfiguration } from "@adyen/adyen-web";
import type { TPaymentMethods } from "@features/checkout/types";
import { EcontextPaymentMethod } from "@lib/constants/payment";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";

// Adyen doesn't actually export the type used for paymentMethodComponents in DropinConfiguration so we have to extract it manually.
type PaymentComponent = Required<DropinConfiguration>["paymentMethodComponents"][number];

/**
 * Mapping of payment method types to their corresponding Adyen component importers.
 * Using separate wrapper files for each component to enable optimal code-splitting.
 * Each wrapper file creates its own webpack chunk, ensuring components load separately.
 *
 * See the ComponentsMap in node_modules/@adyen/adyen-web/dist/es/index.d.ts for reference of payment types.
 */
const PAYMENT_METHOD_COMPONENT_MAP: Record<string, () => Promise<PaymentComponent>> = {
    scheme: () => import("./components/card").then((mod) => mod.Card),
    afterpay: () => import("./components/afterpay").then((mod) => mod.AfterPay),
    afterpaytouch: () => import("./components/afterpay").then((mod) => mod.AfterPay),
    clearpay: () => import("./components/afterpay").then((mod) => mod.AfterPay),
    amazonpay: () => import("./components/amazonpay").then((mod) => mod.AmazonPay),
    applepay: () => import("./components/applepay").then((mod) => mod.ApplePay),
    blik: () => import("./components/blik").then((mod) => mod.Blik),
    giropay: () => import("./components/giropay").then((mod) => mod.Giropay),
    googlepay: () => import("./components/googlepay").then((mod) => mod.GooglePay),
    kcp_banktransfer: () => import("./components/banktransfer").then((mod) => mod.BankTransfer),
    kcp_creditcard: () => import("./components/card").then((mod) => mod.Card),
    mbway: () => import("./components/mbway").then((mod) => mod.MBWay),
    onlineBanking_PL: () =>
        import("./components/onlineBankingPl").then((mod) => mod.OnlineBankingPL),
    paypal: () => import("./components/paypal").then((mod) => mod.PayPal),
    paypay: () => import("./components/redirect").then((mod) => mod.Redirect),
    ideal: () => import("./components/redirect").then((mod) => mod.Redirect),
    kcp_payco: () => import("./components/redirect").then((mod) => mod.Redirect),
    kcp_naverpay: () => import("./components/redirect").then((mod) => mod.Redirect),
    zip: () => import("./components/redirect").then((mod) => mod.Redirect),
    grabpay: () => import("./components/redirect").then((mod) => mod.Redirect),
    grabpay_SG: () => import("./components/redirect").then((mod) => mod.Redirect),
    grabpay_MY: () => import("./components/redirect").then((mod) => mod.Redirect),
    twint: () => import("./components/twint").then((mod) => mod.Twint),

    // Klarna Payments
    klarna: () => import("./components/klarna").then((mod) => mod.Klarna),
    klarna_account: () => import("./components/klarna").then((mod) => mod.Klarna),
    klarna_paynow: () => import("./components/klarna").then((mod) => mod.Klarna),

    // UPI Payments
    upi: () => import("./components/upi").then((mod) => mod.UPI),
    upi_collect: () => import("./components/upi").then((mod) => mod.UPI),
    upi_qr: () => import("./components/upi").then((mod) => mod.UPI),
    upi_intent: () => import("./components/upi").then((mod) => mod.UPI),

    // MobilePay and Vipps is the same thing
    mobilepay: () => import("./components/vipps").then((mod) => mod.Vipps),
    vipps: () => import("./components/vipps").then((mod) => mod.Vipps),
    swish: () => import("./components/swish").then((mod) => mod.Swish),

    // Econtext (Convenience Store Payments)
    econtext: () => import("./components/econtext").then((mod) => mod.Econtext),
    econtext_atm: () => import("./components/econtext").then((mod) => mod.Econtext),
    econtext_online: () => import("./components/econtext").then((mod) => mod.Econtext),
    [EcontextPaymentMethod.SevenEleven]: () =>
        import("./components/econtext").then((mod) => mod.Econtext),
    [EcontextPaymentMethod.Stores]: () =>
        import("./components/econtext").then((mod) => mod.Econtext),
};

/**
 * Dynamically loads Adyen payment method components based on available payment methods.
 * Returns an array of component classes to pass to Dropin configuration.
 * This enables code-splitting while ensuring components are available to Dropin.
 *
 * @param paymentMethods - Array of payment methods from the payments response
 * @returns Promise that resolves to an array of component classes
 */
export const loadPaymentMethodComponents = async (
    paymentMethods: TPaymentMethods[] = []
): Promise<PaymentComponent[]> => {
    const uniqueTypes = new Set(paymentMethods.map((pm) => pm.type).filter(Boolean));

    const componentPromises = Array.from(uniqueTypes).map((type) => {
        const importer = PAYMENT_METHOD_COMPONENT_MAP[type];

        if (!importer) {
            const paymentMethod = paymentMethods.find((pm) => pm.type === type);
            // Payment is not yet implemented here. Silently fail and log for visibility.
            Logger.error(
                ServiceType.WEB,
                "loadPaymentMethodComponents: Unsupported payment method",
                {
                    tag: LogTag.CHECKOUT,
                    type,
                    paymentMethod,
                }
            );
            return null;
        }
        return importer().catch((error) => {
            // Silently fail for unsupported payment methods
            Logger.error(
                ServiceType.WEB,
                "loadPaymentMethodComponents: Failed to load payment method",
                {
                    tag: LogTag.CHECKOUT,
                    type,
                    error,
                }
            );
            return null;
        });
    });

    const loadedComponents = await Promise.all(componentPromises);
    const filteredComponents = loadedComponents.filter(
        (component: PaymentComponent | null): component is PaymentComponent => component !== null
    );

    // Filter out nulls and return unique components (deduplicate by reference)
    return [...new Set(filteredComponents)];
};
